import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Minus, Plus, Trash2, ShoppingCart, ChefHat, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function CartPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { items, totalItems, totalPrice, updateQty, removeItem, clearCart } = useCart();
    const { rtl } = useLanguage();
    const [clearing, setClearing] = useState(false);

    const handleClear = () => {
        setClearing(true);
        setTimeout(() => { clearCart(); setClearing(false); }, 300);
    };

    return (
        <div className="min-h-screen bg-[#0D0D0D] text-white" dir={rtl ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className="sticky top-0 z-40 backdrop-blur-2xl border-b border-white/5" style={{ backgroundColor: 'rgba(13,13,13,0.92)' }}>
                <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate(`/menu/${slug}`)} className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 active:scale-90 transition-all" data-testid="cart-back-btn">
                            <ArrowLeft className="w-[18px] h-[18px] text-white/70" />
                        </button>
                        <div>
                            <h1 className="text-lg font-medium tracking-tight">Your Order</h1>
                            <p className="text-[11px] text-white/30">{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
                        </div>
                    </div>
                    {items.length > 0 && (
                        <button onClick={handleClear} className="text-[11px] text-red-400/70 hover:text-red-400 tracking-wide uppercase transition-colors" data-testid="clear-cart-btn">
                            Clear All
                        </button>
                    )}
                </div>
            </div>

            {/* Cart Items */}
            <div className="max-w-lg mx-auto px-5 py-6 pb-36">
                {items.length === 0 ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-20">
                        <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-white/10" />
                        <p className="text-white/30 text-sm mb-6">Your cart is empty</p>
                        <Button onClick={() => navigate(`/menu/${slug}`)} className="bg-[#C9A227] text-white rounded-full px-8" data-testid="browse-menu-btn">
                            Browse Menu
                        </Button>
                    </motion.div>
                ) : (
                    <AnimatePresence>
                        <div className="space-y-3">
                            {items.map((item, idx) => (
                                <motion.div
                                    key={item.id}
                                    layout
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: clearing ? 0 : 1, x: 0 }}
                                    exit={{ opacity: 0, x: 60, height: 0, marginBottom: 0 }}
                                    transition={{ duration: 0.3, delay: idx * 0.03 }}
                                    className="flex gap-4 p-3 rounded-2xl bg-white/[0.03] border border-white/[0.05]"
                                    data-testid={`cart-item-${item.id}`}
                                >
                                    {/* Thumbnail */}
                                    <div className="w-[72px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 bg-white/5">
                                        {item.image_url ? (
                                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center"><ChefHat className="w-6 h-6 text-white/15" /></div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="text-[14px] font-medium leading-snug truncate">{item.name}</p>
                                            <button onClick={() => removeItem(item.id)} className="text-white/20 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5" data-testid={`remove-item-${item.id}`}>
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <p className="text-[#C9A227] font-semibold text-[15px]">QAR {(item.price * item.qty).toFixed(0)}</p>
                                            {/* Quantity controls */}
                                            <div className="flex items-center gap-0 rounded-full bg-white/[0.06] border border-white/[0.08]">
                                                <button onClick={() => updateQty(item.id, -1)} className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white active:scale-90 transition-all" data-testid={`qty-minus-${item.id}`}>
                                                    <Minus className="w-3.5 h-3.5" />
                                                </button>
                                                <span className="w-7 text-center text-sm font-medium" data-testid={`qty-value-${item.id}`}>{item.qty}</span>
                                                <button onClick={() => updateQty(item.id, 1)} className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white active:scale-90 transition-all" data-testid={`qty-plus-${item.id}`}>
                                                    <Plus className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </AnimatePresence>
                )}
            </div>

            {/* Sticky Footer */}
            {items.length > 0 && (
                <motion.div
                    initial={{ y: 60 }}
                    animate={{ y: 0 }}
                    className="fixed bottom-0 left-0 right-0 backdrop-blur-2xl border-t border-white/5"
                    style={{ backgroundColor: 'rgba(13,13,13,0.95)' }}
                >
                    <div className="max-w-lg mx-auto px-5 py-5">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-white/40 text-sm">Total</span>
                            <span className="text-xl font-semibold text-[#C9A227]" data-testid="cart-total">QAR {totalPrice.toFixed(0)}</span>
                        </div>
                        <Button
                            onClick={() => navigate(`/menu/${slug}/order`)}
                            className="w-full h-[52px] rounded-2xl text-[15px] font-semibold bg-[#C9A227] text-white hover:bg-[#b8922a]"
                            style={{ boxShadow: '0 8px 30px rgba(201,162,39,0.25)' }}
                            data-testid="continue-btn"
                        >
                            Continue
                        </Button>
                    </div>
                </motion.div>
            )}
        </div>
    );
}
